import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useConfirm } from '../ui/ConfirmDialog';
import {
  KNOWLEDGE_SOURCE_TYPES,
  addKnowledgeSource,
  removeKnowledgeSource,
  reindexKnowledgeSource,
  getKnowledgeSet,
  type KnowledgeSetDoc,
  type KnowledgeSourceType,
} from '../../lib/aiAgents';

interface KnowledgeSourcesDrawerProps {
  open: boolean;
  knowledgeSetId: string | null;
  onClose: () => void;
  onUpdated: () => void;
}

export function KnowledgeSourcesDrawer({
  open,
  knowledgeSetId,
  onClose,
  onUpdated,
}: KnowledgeSourcesDrawerProps) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [setDoc, setSetDoc] = useState<KnowledgeSetDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // New source form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [sourceType, setSourceType] = useState<KnowledgeSourceType>('content');
  const [sourceName, setSourceName] = useState('');
  const [contentText, setContentText] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [filePath, setFilePath] = useState('');
  const [mediaPath, setMediaPath] = useState('');
  const [sheetId, setSheetId] = useState('');
  const [sheetTab, setSheetTab] = useState('');
  const [apiId, setApiId] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchDetail = async () => {
    if (!knowledgeSetId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getKnowledgeSet(knowledgeSetId);
      setSetDoc(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load knowledge set');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && knowledgeSetId) {
      fetchDetail();
      setShowAddForm(false);
      resetForm();
    }
  }, [open, knowledgeSetId]);

  const resetForm = () => {
    setSourceName('');
    setContentText('');
    setSourceUrl('');
    setFilePath('');
    setMediaPath('');
    setSheetId('');
    setSheetTab('');
    setApiId('');
    setSourceType('content');
  };

  const handleAddSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!knowledgeSetId) return;
    if (!sourceName.trim()) {
      setError(t('ai.sources.nameRequired'));
      return;
    }

    const config: any = {};
    if (sourceType === 'content') {
      if (!contentText.trim()) {
        setError(t('ai.sources.contentRequired'));
        return;
      }
      config.text = contentText.trim();
    } else if (sourceType === 'url') {
      if (!sourceUrl.trim()) {
        setError(t('ai.sources.urlRequired'));
        return;
      }
      config.url = sourceUrl.trim();
    } else if (sourceType === 'file') {
      config.fileName = sourceName.trim();
      config.storagePath = filePath.trim() || undefined;
    } else if (sourceType === 'media') {
      config.fileName = sourceName.trim();
      config.storagePath = mediaPath.trim() || undefined;
    } else if (sourceType === 'google_sheet') {
      if (!sheetId.trim()) {
        setError(t('ai.sources.sheetRequired'));
        return;
      }
      config.sheetId = sheetId.trim();
      config.tab = sheetTab.trim() || undefined;
    } else if (sourceType === 'api') {
      if (!apiId.trim()) {
        setError(t('ai.sources.apiRequired'));
        return;
      }
      config.apiId = apiId.trim();
    }

    setAdding(true);
    setError(null);
    try {
      await addKnowledgeSource({
        knowledgeSetId,
        type: sourceType,
        name: sourceName.trim(),
        config,
      });
      resetForm();
      setShowAddForm(false);
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to add source');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveSource = async (sourceId: string) => {
    if (!knowledgeSetId) return;
    if (!(await confirm({ title: t('common.areYouSure'), body: t('ai.sources.removeConfirm'), confirmLabel: t('common.remove'), danger: true }))) return;

    setActionBusy(sourceId);
    setError(null);
    try {
      await removeKnowledgeSource(knowledgeSetId, sourceId);
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to remove source');
    } finally {
      setActionBusy(null);
    }
  };

  const handleReindexSource = async (sourceId: string) => {
    if (!knowledgeSetId) return;
    setActionBusy(sourceId);
    setError(null);
    try {
      await reindexKnowledgeSource(knowledgeSetId, sourceId);
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to reindex source');
    } finally {
      setActionBusy(null);
    }
  };

  const sources = setDoc?.sources || [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={setDoc ? `${setDoc.name} — ${t('ai.sources.title')}` : t('ai.sources.title')}
      size="lg"
    >
      <div className="space-y-6">
        {error && (
          <div className="rounded-lg bg-danger-50 p-3 text-xs text-danger dark:bg-rose-950/40 dark:text-rose-400">
            {error}
          </div>
        )}

        {/* Set Header summary */}
        {setDoc && (
          <div className="rounded-xl border border-line bg-surface-subtle p-4 dark:border-slate-700 dark:bg-slate-800/40">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-base font-bold text-ink dark:text-white">{setDoc.name}</h3>
                {setDoc.description && (
                  <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">{setDoc.description}</p>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="rounded-md bg-brand-50 px-2 py-1 font-semibold text-brand dark:bg-brand-950/50 dark:text-brand-tint">
                  {setDoc.totalChunks || 0} {t('ai.knowledge.totalChunks')}
                </span>
                <span className="rounded-md bg-slate-100 px-2 py-1 font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  {sources.length} {t('ai.knowledge.sources')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Sources list */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-bold text-ink dark:text-white">
              {t('ai.sources.title')} ({sources.length})
            </h4>
            {!showAddForm && (
              <Button size="sm" variant="primary" onClick={() => setShowAddForm(true)}>
                + {t('ai.sources.add')}
              </Button>
            )}
          </div>

          {/* Add Source inline form */}
          {showAddForm && (
            <form
              onSubmit={handleAddSource}
              className="mb-6 rounded-xl border border-brand/40 bg-surface p-4 shadow-sm dark:border-brand-tint/40 dark:bg-slate-800"
            >
              <h5 className="mb-3 text-xs font-bold text-brand dark:text-brand-tint">
                + {t('ai.sources.add')}
              </h5>

              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                      {t('ai.sources.type')}
                    </label>
                    <select
                      value={sourceType}
                      onChange={(e) => setSourceType(e.target.value as KnowledgeSourceType)}
                      className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    >
                      {KNOWLEDGE_SOURCE_TYPES.map((st) => (
                        <option key={st} value={st}>
                          {t(`ai.sources.${st}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                      {t('ai.sources.name')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={sourceName}
                      onChange={(e) => setSourceName(e.target.value)}
                      placeholder={t('ai.sources.namePlaceholder') || 'e.g. Return Policy FAQ'}
                      className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Conditional Inputs per Source Type */}
                {sourceType === 'content' && (
                  <div>
                    <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                      {t('ai.sources.content')} *
                    </label>
                    <textarea
                      rows={5}
                      required
                      value={contentText}
                      onChange={(e) => setContentText(e.target.value)}
                      placeholder={t('ai.sources.contentPlaceholder')}
                      className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                )}

                {sourceType === 'url' && (
                  <div>
                    <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                      {t('ai.sources.url')} *
                    </label>
                    <input
                      type="url"
                      required
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                      placeholder={t('ai.sources.urlPlaceholder')}
                      className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                )}

                {sourceType === 'file' && (
                  <div>
                    <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                      {t('ai.sources.file')}
                    </label>
                    <input
                      type="text"
                      value={filePath}
                      onChange={(e) => setFilePath(e.target.value)}
                      placeholder={t('ai.sources.filePlaceholder')}
                      className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                )}

                {sourceType === 'media' && (
                  <div>
                    <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                      {t('ai.sources.media')}
                    </label>
                    <input
                      type="text"
                      value={mediaPath}
                      onChange={(e) => setMediaPath(e.target.value)}
                      placeholder={t('ai.sources.mediaPlaceholder')}
                      className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                )}

                {sourceType === 'google_sheet' && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                        {t('ai.sources.google_sheet')} *
                      </label>
                      <input
                        type="text"
                        required
                        value={sheetId}
                        onChange={(e) => setSheetId(e.target.value)}
                        placeholder={t('ai.sources.sheetPlaceholder')}
                        className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                        {t('ai.sources.tabPlaceholder')}
                      </label>
                      <input
                        type="text"
                        value={sheetTab}
                        onChange={(e) => setSheetTab(e.target.value)}
                        placeholder="Sheet1"
                        className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </div>
                  </div>
                )}

                {sourceType === 'api' && (
                  <div>
                    <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300">
                      {t('ai.sources.api')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={apiId}
                      onChange={(e) => setApiId(e.target.value)}
                      placeholder={t('ai.sources.apiPlaceholder')}
                      className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowAddForm(false)}
                    disabled={adding}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" size="sm" variant="primary" loading={adding}>
                    {t('ai.sources.add')}
                  </Button>
                </div>
              </div>
            </form>
          )}

          {/* Sources table / cards */}
          {loading ? (
            <div className="py-8 text-center text-xs text-ink-3">…</div>
          ) : sources.length === 0 ? (
            <div className="rounded-xl border border-dashed border-line p-8 text-center dark:border-slate-700">
              <p className="text-xs font-semibold text-ink dark:text-white">{t('ai.sources.empty')}</p>
              <p className="mt-1 text-[11px] text-ink-3 dark:text-slate-400">{t('ai.sources.emptyDesc')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sources.map((s) => (
                <div
                  key={s.id}
                  className="flex flex-col gap-2 rounded-xl border border-line bg-surface p-3 transition hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800/80 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-xs text-ink dark:text-white">{s.name}</span>
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        {t(`ai.sources.${s.type}`)}
                      </span>
                      {s.status === 'ready' && <Badge variant="success">{t('ai.agents.active')}</Badge>}
                      {s.status === 'indexing' && <Badge variant="info">{t('ai.sources.indexing') || 'Indexing…'}</Badge>}
                      {s.status === 'error' && <Badge variant="danger">{t('common.error') || 'Error'}</Badge>}
                      {s.status === 'pending' && <Badge variant="warning">{t('subs.status.pending') || 'Pending'}</Badge>}
                    </div>

                    <div className="flex items-center gap-3 text-[11px] text-ink-3 dark:text-slate-400">
                      <span>{t('ai.chunksCount', { count: s.chunkCount || 0 }) || `${s.chunkCount || 0} chunks`}</span>
                      {s.error && <span className="text-danger">{s.error}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleReindexSource(s.id)}
                      loading={actionBusy === s.id}
                      disabled={Boolean(actionBusy)}
                      title={t('ai.sources.reindex')}
                    >
                      {t('ai.sources.reindex')}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleRemoveSource(s.id)}
                      disabled={Boolean(actionBusy)}
                      title={t('ai.sources.remove')}
                    >
                      {t('ai.sources.remove')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
